// 存储所有活动的EventSource连接
const eventSources = new Map<string, EventSource>();

// 存储事件监听器
const eventListeners = new Map<string, Set<(data: any) => void>>();

class SSEService {
  private connected = false;
  private currentUserId: string | null = null;

  constructor() {}

  /**
   * 注册事件监听器
   * @param eventType 事件类型
   * @param listener 监听器函数
   */
  public on(eventType: string, listener: (data: any) => void): void {
    if (!eventListeners.has(eventType)) {
      eventListeners.set(eventType, new Set());
    }

    const listeners = eventListeners.get(eventType)!;
    listeners.add(listener);
  }

  /**
   * 移除事件监听器
   * @param eventType 事件类型
   * @param listener 监听器函数
   */
  public off(eventType: string, listener: (data: any) => void): void {
    const listeners = eventListeners.get(eventType);
    if (listeners) {
      listeners.delete(listener);

      // 如果没有监听器了，清理Set
      if (listeners.size === 0) {
        eventListeners.delete(eventType);
      }
    }
  }

  /**
   * 连接到SSE流
   * @param userId 用户ID
   */
  public async connect(userId: string): Promise<void> {
    // 参数校验
    if (!userId) {
      console.error("[SSE Service] UserId is required");
      throw new Error("UserId is required");
    }

    // 如果已经连接但用户ID不同，先断开连接
    if (this.connected && this.currentUserId !== userId) {
      this.disconnect();
    }

    // 如果已经连接且用户ID相同，直接返回
    if (this.connected && this.currentUserId === userId) {
      return;
    }

    this.currentUserId = userId;
    try {
      // 使用基于Durable Object的新路由
      const eventSource = new EventSource(`/api/stream?userId=${userId}`);
      eventSources.set("global", eventSource);

      // 设置连接状态
      this.connected = true;

      // 监听消息事件
      eventSource.onmessage = event => {
        try {
          const data = JSON.parse(event.data);

          // 处理特殊事件类型
          if (data.type === "sse_connected") {
            return;
          }

          if (data.type === "error") {
            console.error("[SSE Service] Error received from server", data);
            return;
          }
          // 直接分发业务事件
          this.dispatchEvent(data.type, data);
        } catch (error) {
          console.error("[SSE Service] Error parsing SSE message:", error);
        }
      };
    } catch (error) {
      console.error("[SSE Service] Failed to create EventSource:", error);
      this.connected = false;
    }
  }

  /**
   * 断开SSE连接
   */
  public disconnect(): void {
    // 关闭EventSource连接
    const eventSource = eventSources.get("global");
    if (eventSource) {
      eventSource.close();
      eventSources.delete("global");
    }

    // 重置状态
    this.connected = false;
    this.currentUserId = null;
  }

  /**
   * 直接分发事件给监听器
   * @param eventType 事件类型
   * @param data 事件数据
   */
  private dispatchEvent(eventType: string, data: any): void {
    const listeners = eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          console.error(`[SSE Service] Error in listener for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * 检查连接状态
   */
  public isConnected(): boolean {
    return this.connected;
  }

  /**
   * 获取当前用户ID
   */
  public getCurrentUserId(): string | null {
    return this.currentUserId;
  }
}

export const sseService = new SSEService();

// 页面卸载时断开所有连接
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    sseService.disconnect();
  });
}
